import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ExportFormatButtons } from '@/components/buttons/ExportFormatButton';
import { ArtifactContent } from '@/components/ArtifactContent';
import { AgentCanvasOverlay, type AgentCanvasOverlayHandle } from '@/components/AgentCanvasOverlay';
import { useDevPanelSplit } from '@/lib/devPanelSplit';
import { ArtifactEmptyState } from '@/components/labels/ArtifactEmptyState';
import { applyTheme } from '@/artifact/theme';
import { usePaperTheme } from '@/hooks/useThemeFlag';
import { useDevBannerOverrides } from '@/lib/devBannerTriggers';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { PILL_GUTTER, PILL_RAIL_OFFSET } from './railConstants';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_COMPACT_CLASS,
  PAPER_ICON_CONTROL_COMPACT_CLASS,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
  PAPER_MOBILE_HEADER_GRID_CLASS,
  PAPER_MOBILE_HEADER_TITLE_CAP_CLASS,
} from './paperShapes';
import { downloadExportPayload, exportSurfaceSnapshot, type ArtifactExportFormat } from '@/artifact/export';
import type { ArtifactEngine } from '@/hooks/useArtifactEngine';
import { trackArtifactExported } from '@/analytics';
import '@/artifact/artifact.css';

export interface PaperArtifactPanelProps {
  engine: ArtifactEngine;
  maximized: boolean;
  onMaximizedChange: (next: boolean) => void;
  onClose: () => void;
  agentTitle?: string | null;
}

const PANEL_WIDTH = 400;
const MIN_WIDTH = 320;
/** Approximate horizontal space taken by the left + right rails. Used to
 *  exclude rails from the chat-vs-panel split calculation. */
const RAIL_ALLOWANCE = 120;

interface ExpandToggleButtonProps {
  maximized: boolean;
  onClick: () => void;
}

const ExpandToggleButton = (props: ExpandToggleButtonProps) => (
  <button
    type="button"
    onClick={props.onClick}
    title={props.maximized ? 'Restore split view' : 'Expand over chat'}
    aria-label={props.maximized ? 'Restore split view' : 'Expand panel over chat'}
    aria-pressed={props.maximized}
    aria-controls="paper-artifact-panel"
    className={cn(
      PAPER_ICON_CONTROL_COMPACT_CLASS,
      'shrink-0',
    )}
  >
    {props.maximized ? (
      <Minimize2 className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
    ) : (
      <Maximize2 className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
    )}
  </button>
);

interface PanelCloseButtonProps {
  onClick: () => void;
}

const PanelCloseButton = (props: PanelCloseButtonProps) => (
  <button
    type="button"
    onClick={props.onClick}
    title="Close panel"
    aria-label="Close panel"
    aria-controls="paper-artifact-panel"
    className={cn(
      PAPER_ICON_CONTROL_COMPACT_CLASS,
      'ml-[4px]',
    )}
  >
    <X className={PAPER_ICON_COMPACT_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
  </button>
);

export const PaperArtifactPanel = (props: PaperArtifactPanelProps) => {
  const { engine, maximized, onMaximizedChange, onClose, agentTitle } = props;
  const {
    store,
    rootCuids,
    hasComponents,
    callbacks,
    theme,
    setSurfaceGetter,
    sendAgentCanvasSubmission,
    setAgentCanvasController,
  } = engine;

  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const agentCanvasRef = useRef<AgentCanvasOverlayHandle>(null);

  useEffect(() => {
    setAgentCanvasController({
      collectContext: async () => agentCanvasRef.current?.collectContext() ?? null,
      clear: () => agentCanvasRef.current?.clear(),
    });
    return () => setAgentCanvasController(null);
  }, [setAgentCanvasController]);
  const [exportingFormat, setExportingFormat] = useState<ArtifactExportFormat | null>(null);

  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  const dragPointerId = useRef<number | null>(null);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  // Re-derive panel width from the dev-controlled split ratio whenever the
  // ratio changes (or on first mount). Manual drag wins until the ratio is
  // changed again, at which point we snap back to ratio × available.
  const splitRatio = useDevPanelSplit();
  const lastAppliedRatio = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (lastAppliedRatio.current === splitRatio) return;
    const parent = sectionRef.current?.parentElement;
    if (!parent) return;
    const available = parent.getBoundingClientRect().width - RAIL_ALLOWANCE;
    if (available <= 0) return;
    setPanelWidth(Math.max(MIN_WIDTH, available * splitRatio));
    lastAppliedRatio.current = splitRatio;
  }, [splitRatio]);

  const handleDragStart = useCallback((e: React.PointerEvent) => {
    if (maximized) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragPointerId.current = e.pointerId;
    dragStartX.current = e.clientX;
    dragStartWidth.current = sectionRef.current?.offsetWidth ?? PANEL_WIDTH;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [maximized]);

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (dragPointerId.current === null) return;
    const delta = dragStartX.current - e.clientX;
    const next = Math.max(MIN_WIDTH, dragStartWidth.current + delta);
    setPanelWidth(next);
  }, []);

  const handleDragEnd = useCallback((e: React.PointerEvent) => {
    if (dragPointerId.current === null) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* already released */ }
    dragPointerId.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const effectiveWidth = panelWidth ?? PANEL_WIDTH;

  const getSurfaceElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    const el = containerRef.current.querySelector('.a-surface');
    return el instanceof HTMLElement ? el : null;
  }, []);

  useEffect(() => {
    setSurfaceGetter(getSurfaceElement);
    return () => setSurfaceGetter(null);
  }, [getSurfaceElement, setSurfaceGetter]);

  const paperTheme = usePaperTheme();
  const devOverrides = useDevBannerOverrides();
  const forceArtifact = devOverrides.forceArtifact;
  const pill = devOverrides.railPill;
  useLayoutEffect(() => {
    if (containerRef.current) applyTheme(theme, containerRef.current, true);
  }, [theme, maximized, paperTheme]);

  const handleDownload = useCallback(
    async (format: ArtifactExportFormat) => {
      const surface = getSurfaceElement();
      if (!surface || exportingFormat !== null) return;
      trackArtifactExported(format);
      setExportingFormat(format);
      try {
        const payload = await exportSurfaceSnapshot(surface, format);
        const stamp = new Date().toISOString().replaceAll(':', '-');
        downloadExportPayload(payload, `panel-${stamp}.${format}`);
      } finally {
        setExportingFormat(null);
      }
    },
    [exportingFormat, getSurfaceElement],
  );

  const isMobile = useIsMobileDevice();
  // On mobile the panel is the only column visible (PaperChatLayout hides
  // the chat column when artifactOpen on mobile), so let it flex to fill
  // instead of pinning a desktop-sized 400px width.
  const widthStyle = maximized || isMobile
    ? undefined
    : { width: effectiveWidth, minWidth: MIN_WIDTH };

  return (
    <section
      ref={sectionRef}
      id="paper-artifact-panel"
      aria-label="Panel"
      className={cn(
        'flex flex-col shrink-0 relative min-w-0',
        'bg-[var(--bg)] text-[var(--fg)]',
        (maximized || isMobile) && 'flex-1',
      )}
      style={widthStyle}
    >
      {!maximized && !isMobile && (
        <div
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          className={cn(
            'absolute top-0 bottom-0 z-20 cursor-col-resize group',
            'w-[6px] -left-[3px]',
          )}
          style={{ touchAction: 'none' }}
        >
          <div
            className={cn(
              'absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors duration-150',
              'bg-transparent group-hover:bg-[var(--periwinkle)]',
            )}
          />
        </div>
      )}
      <div
        className={cn(
          'flex items-stretch border-b border-solid border-[var(--hair)] shrink-0',
        )}
        style={pill ? { marginRight: -PILL_RAIL_OFFSET } : undefined}
      >
        {isMobile ? (
          /* Mobile: Back button on the left returns to the chat, the
             artifact title centers across the top bar, exports collapse
             to the right. No maximize toggle — the panel is always
             fullscreen on mobile since the chat column is hidden
             behind it. */
          <div
            className={cn(
              'grid items-center flex-1 min-w-0 gap-[8px]',
              PAPER_MOBILE_HEADER_GRID_CLASS,
            )}
            style={{ padding: '10px 12px' }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Back to chat"
              className={cn(
                'justify-self-start',
                'h-[30px] inline-flex items-center gap-[4px] shrink-0',
                PAPER_CONTROL_RADIUS_CLASS,
                'px-[8px] text-[12px] text-[var(--fg-2)]',
                'bg-transparent border-0 cursor-pointer',
                'hover:bg-[var(--bg-alt)] hover:text-[var(--fg)]',
                'transition-colors duration-150',
              )}
            >
              <ArrowLeft className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
              Back
            </button>
            <div className={cn(
              'justify-self-center min-w-0 flex items-center justify-center',
              PAPER_MOBILE_HEADER_TITLE_CAP_CLASS,
            )}>
              {agentTitle && (
                <span
                  className="text-[15px] text-[var(--fg)] truncate max-w-full"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {agentTitle}
                </span>
              )}
            </div>
            <div className="justify-self-end min-w-0 flex items-center justify-end shrink-0 h-[28px]">
              {hasComponents && (
                <ExportFormatButtons
                  formats={['png', 'pdf', 'html']}
                  disabled={exportingFormat !== null}
                  onDownload={handleDownload}
                  compact
                  buttonClassName={cn(
                    PAPER_ICON_CONTROL_COMPACT_CLASS,
                  )}
                />
              )}
            </div>
          </div>
        ) : maximized ? (
          /* Maximized layout: exit-fullscreen toggle anchored on the
             left, the agent title centered across the page, exports +
             close on the right. In pill mode the close (X) is hoisted
             out of this row so it can sit at the far-right edge of the
             chrome (visually over the canvas rail extension). */
          <div
            className="flex items-center flex-1 min-w-0"
            style={{ padding: '14px 6px 14px 18px' }}
          >
            <div className="flex-1 min-w-0 flex items-center h-[28px]">
              <ExpandToggleButton maximized onClick={() => onMaximizedChange(false)} />
            </div>
            {agentTitle && (
              <div className="shrink-0 flex items-center gap-[10px] min-w-0 px-[14px]">
                <span className="text-[11px] tracking-[0.14em] uppercase text-[var(--fg-3)] shrink-0">
                  Agent
                </span>
                <span
                  className="text-[16px] text-[var(--fg)] truncate"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {agentTitle}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0 flex items-center justify-end gap-[2px] h-[28px]">
              {hasComponents && (
                <ExportFormatButtons
                  formats={['png', 'pdf', 'html']}
                  disabled={exportingFormat !== null}
                  onDownload={handleDownload}
                  buttonClassName={cn(
                    'h-[22px] px-[6px] text-[10px] tracking-[0.1em]',
                    PAPER_CONTROL_RADIUS_CLASS,
                    'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)]',
                    'transition-colors duration-150',
                  )}
                />
              )}
              {!pill && <PanelCloseButton onClick={onClose} />}
            </div>
          </div>
        ) : (
          /* Split-view layout: maximize toggle on the left, exports +
             close on the right. The close (X) lives in this chrome row
             — not in the right rail — so the canvas tools rail stays
             dedicated to drawing tools. In floating rail mode the close is
             hoisted to the far-right edge of the chrome (see below). */
          <div
            className="flex flex-1 items-center justify-between min-w-0"
            style={{ padding: '14px 6px 14px 18px' }}
          >
            <div className="flex items-center gap-[10px] h-[28px] min-w-0">
              <ExpandToggleButton maximized={false} onClick={() => onMaximizedChange(true)} />
            </div>
            <div className="flex items-center gap-[2px] h-[28px] shrink-0">
              {hasComponents && (
                <ExportFormatButtons
                  formats={['png', 'pdf', 'html']}
                  disabled={exportingFormat !== null}
                  onDownload={handleDownload}
                  buttonClassName={cn(
                    'h-[22px] px-[6px] text-[10px] tracking-[0.1em]',
                    PAPER_CONTROL_RADIUS_CLASS,
                    'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)]',
                    'transition-colors duration-150',
                  )}
                />
              )}
              {!pill && <PanelCloseButton onClick={onClose} />}
            </div>
          </div>
        )}
        {pill && (
          /* In pill mode the chrome extends PILL_RAIL_OFFSET past the
             panel proper (over the canvas rail's gutter). Park the
             close (X) in that extension, aligned with the rail's right
             edge so it reads as the natural terminal of the title bar. */
          <div
            className="shrink-0 flex items-center justify-end"
            style={{ width: PILL_RAIL_OFFSET, paddingRight: PILL_GUTTER }}
          >
            <PanelCloseButton onClick={onClose} />
          </div>
        )}
      </div>

      <ArtifactContent
        store={store}
        callbacks={callbacks}
        rootCuids={rootCuids}
        containerRef={containerRef}
        emptyState={<ArtifactEmptyState title="No panel yet" />}
        forceOverlay={forceArtifact}
        overlay={
          <AgentCanvasOverlay
            ref={agentCanvasRef}
            active={true}
            initialTool={null}
            containerRef={containerRef}
            onSubmit={sendAgentCanvasSubmission}
          />
        }
      />
    </section>
  );
};
